import React, { useState } from "react";
import { Form, Input, Button, message, Card, Typography, Modal } from "antd";
import { UserOutlined, LockOutlined, CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import EnterpriseLoyaltyAPI from "../../../api/EnterpriseLoyalty";
import "./EnterpriseLogin.scss";

const { Title, Text } = Typography;

const EnterpriseLogin = () => {
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const [form] = Form.useForm();
  const [show2FA, setShow2FA] = useState(false);
  const [loginValues, setLoginValues] = useState(null);
  const [merchantModalVisible, setMerchantModalVisible] = useState(false);
  const [merchantData, setMerchantData] = useState(null);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Try to login with organization credentials
      const response = await EnterpriseLoyaltyAPI.organizationLogin(
        values.email,
        values.password
      );

      if (response.two_factor_required) {
        setShow2FA(true);
        setLoginValues(values);
        message.info("Please enter your 2FA token to continue.");
        return;
      }

      message.success("Login successful!");
      // Redirect to enterprise dashboard
      history.push("/e/dashboard");
    } catch (error) {
      console.error("Organization login failed:", error);
      // ... existing error handling ...
      try {
        const merchantResponse = await EnterpriseLoyaltyAPI.tryMerchantLogin(
          values.email,
          values.password
        );
        
        if (merchantResponse && merchantResponse.data) {
          setMerchantData({
            ...merchantResponse.data,
            email: values.email,
            password: values.password
          });
          setMerchantModalVisible(true);
        } else {
          message.error("Invalid email or password. Please try again.");
        }
      } catch (merchantError) {
        message.error("Login failed. Check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (values) => {
    setLoading(true);
    try {
      await EnterpriseLoyaltyAPI.login2FA(loginValues.email, loginValues.password, values.token);
      message.success("Success! You are now logged in.");
      history.push("/e/dashboard");
    } catch (error) {
      message.error(error.message || "Invalid 2FA token. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = () => {
    setMerchantModalVisible(false);
    // Navigate to signup page with prefilled merchant data
    history.push({
      pathname: "/e/signup",
      state: { merchantData }
    });
  };

  const handleCancelModal = () => {
    setMerchantModalVisible(false);
    setMerchantData(null);
  };

  return (
    <div className="enterprise-login-container">
      <div className="enterprise-login-background">
        <div className="background-overlay" />
      </div>
      
      <div className="enterprise-login-content">
        <Card className="login-card">
          <div className="login-header">
            <div className="logo-container">
              <img 
                src="/loystar-favicon-png.png" 
                alt="Loystar" 
                className="login-logo"
              />
            </div>
            <Title level={2} className="login-title">
              Enterprise Loyalty Suite
            </Title>
            <Text className="login-subtitle">
              Sign in to manage your organization's loyalty program
            </Text>
          </div>

          <Form
            form={form}
            name="enterprise-login"
            onFinish={(values) => {
              if (show2FA) {
                handle2FASubmit(values);
              } else {
                handleSubmit(values);
              }
            }}
            layout="vertical"
            className="login-form"
            size="large"
          >
            {show2FA ? (
              <>
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <Text strong>Two-Factor Authentication</Text>
                  <br />
                  <Text type="secondary">Enter the 6-digit code from your Google Authenticator app</Text>
                </div>
                <Form.Item
                  name="token"
                  rules={[
                    { required: true, message: "Please enter your 2FA token" },
                    { pattern: /^\d{6}$/, message: "Token must be a 6-digit number" }
                  ]}
                >
                  <Input
                    prefix={<LockOutlined className="input-icon" />}
                    placeholder="Enter 6-digit code"
                    autoFocus
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="login-button"
                    loading={loading}
                    block
                  >
                    {loading ? "Verifying..." : "Verify & Sign In"}
                  </Button>
                  <Button 
                    type="link" 
                    onClick={() => setShow2FA(false)} 
                    block 
                    style={{ marginTop: 8 }}
                  >
                    Back to Login
                  </Button>
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your organization email",
                    },
                    {
                      type: "email",
                      message: "Please enter a valid email address",
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="input-icon" />}
                    placeholder="Organization Email"
                    autoComplete="email"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your password",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className="input-icon" />}
                    placeholder="Password"
                    autoComplete="current-password"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="login-button"
                    loading={loading}
                    block
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form>

          {!show2FA && (
            <div className="login-footer">
              <Text className="footer-text">
                Don't have an organization account? <a onClick={() => history.push("/e/signup")}>Sign Up</a>
              </Text>
            </div>
          )}
        </Card>

        <div className="powered-by">
          <Text className="powered-text">
            Powered by Loystar Enterprise © {new Date().getFullYear()}
          </Text>
        </div>
      </div>

      {/* Merchant Account Found Modal */}
      <Modal
        visible={merchantModalVisible}
        onCancel={handleCancelModal}
        footer={null}
        centered
        className="merchant-modal"
      >
        <div className="modal-content">
          <div className="modal-icon">
            <InfoCircleOutlined style={{ fontSize: 48 }} />
          </div>
          <Title level={3} className="modal-title">
            Merchant Account Found
          </Title>
          <Text className="modal-description">
            We found a Loystar merchant account associated with this email. 
            To access the Enterprise Loyalty Suite, you need to create an organization profile.
          </Text>
          {merchantData && (
            <div className="merchant-info">
              <p><strong>Business:</strong> {merchantData.business_name || 'N/A'}</p>
              <p><strong>Email:</strong> {merchantData.email || 'N/A'}</p>
            </div>
          )}
          <div className="modal-actions">
            <Button 
              type="primary" 
              size="large" 
              onClick={handleCreateOrganization}
              icon={<CheckCircleOutlined />}
              block
            >
              Create Organization Profile
            </Button>
            <Button 
              size="large" 
              onClick={handleCancelModal}
              style={{ marginTop: 12 }}
              block
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EnterpriseLogin;
